import { useLocation } from 'react-router';

import { EXPERIENCE_KEYS } from '@studio/utils/experience';
import { memoizeWeakKey } from '@studio/utils/memoize';
import {
  parseSearchParams,
  type SearchParams,
} from '@studio/utils/searchParams';

import useNavigate, { type NavigateFunction } from './useNavigate';

export { type SearchParams };

type NextSearchParams = ConstructorParameters<typeof URLSearchParams>[0];

type SetParamsFromPrevFn = (prevSearchParams: SearchParams) => NextSearchParams;
type SetSearchParamsArg = NextSearchParams | SetParamsFromPrevFn;

export type SetParamsFn = (params: SetSearchParamsArg) => void;

const getSearchParamsSetter = memoizeWeakKey(
  (navigate: NavigateFunction): SetParamsFn => {
    return function setSearchParams(nextSearchParams) {
      navigate(
        prevLocation => {
          const prevUrlSearchParams = new URLSearchParams(prevLocation.search);

          const nextUrlSearchParams = new URLSearchParams(
            typeof nextSearchParams === 'function'
              ? nextSearchParams(Object.fromEntries(prevUrlSearchParams))
              : nextSearchParams,
          );

          // Copy over any experience keys that are not in the new search params
          for (const experienceKey of EXPERIENCE_KEYS) {
            const prevExperienceValue = prevUrlSearchParams.get(experienceKey);
            if (
              prevExperienceValue !== null &&
              !nextUrlSearchParams.has(experienceKey)
            ) {
              nextUrlSearchParams.set(experienceKey, prevExperienceValue);
            }
          }

          return { search: nextUrlSearchParams.toString() };
        },
        { mode: 'replace' },
      );
    };
  },
);

export default function useSearchParams(): readonly [
  searchParams: SearchParams,
  setSearchParams: SetParamsFn,
] {
  const location = useLocation();
  const navigate = useNavigate();

  const searchParams = parseSearchParams(location.search);
  const setSearchParams = getSearchParamsSetter(navigate);

  return [searchParams, setSearchParams] as const;
}
